import { openGaps, type Interval } from '@salon/scheduling';
import { useEffect, useMemo, useState } from 'react';
import type { CalendarData } from '../../lib/calendar';
import { useLocale, useT } from '../../lib/i18n';
import { addDays, dateOf, formatTime, instantAt, minuteOfDay, startOfWeek } from '../../lib/time';
import { formatEuro } from '../forms';

/**
 * The column beside the agenda on wide screens: a month to jump around in, what the
 * day (or week) holds, and where there is still room, per person, to book into.
 */
export function DayColumn({
  data,
  days,
  view,
  date,
  today,
  tz,
  now,
  showRevenue,
  staffIds,
  onPickDate,
  onSlot,
}: {
  data: CalendarData | undefined;
  days: string[];
  view: 'dag' | 'week' | 'lijst';
  date: string;
  today: string;
  tz: string;
  now: number;
  showRevenue: boolean;
  /** The people whose open gaps to show (the columns of the day view). */
  staffIds: string[];
  onPickDate: (date: string) => void;
  onSlot: (staffId: string, date: string, minute: number) => void;
}) {
  return (
    <aside className="flex flex-col gap-4">
      <MiniMonth date={date} today={today} highlightWeek={view !== 'dag'} onPick={onPickDate} />
      {data && <Summary data={data} days={days} view={view} tz={tz} showRevenue={showRevenue} />}
      {data && view === 'dag' && date >= today && <OpenGaps data={data} date={date} today={today} tz={tz} now={now} staffIds={staffIds} onSlot={onSlot} />}
    </aside>
  );
}

const card = 'rounded-[var(--radius-card)] border border-line bg-surface p-4 shadow-[0_1px_2px_rgb(29_25_21/0.04)]';

function MiniMonth({ date, today, highlightWeek, onPick }: { date: string; today: string; highlightWeek: boolean; onPick: (date: string) => void }) {
  const t = useT();
  const locale = useLocale();
  const tag = locale === 'nl' ? 'nl-NL' : 'en-GB';
  const [month, setMonth] = useState(date.slice(0, 7));
  useEffect(() => setMonth(date.slice(0, 7)), [date]);

  const first = `${month}-01`;
  const gridStart = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  // Drop a sixth row that lies completely in the next month.
  const shown = cells[35]!.slice(0, 7) !== month ? cells.slice(0, 35) : cells;
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(tag, { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${addDays(gridStart, i)}T12:00:00Z`)).slice(0, 2),
  );
  const title = new Intl.DateTimeFormat(tag, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${first}T12:00:00Z`));
  const shift = (n: number) => setMonth(addDays(`${month}-15`, n * 30).slice(0, 7));
  const selectedWeek = startOfWeek(date);

  return (
    <section className={card} aria-label={title}>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="display text-[26px] leading-none first-letter:uppercase">{title}</h2>
        <span className="flex">
          <button type="button" aria-label={t('cal.side.prevMonth')} onClick={() => shift(-1)} className="flex size-9 items-center justify-center rounded-full text-lg hover:bg-subtle">
            ‹
          </button>
          <button type="button" aria-label={t('cal.side.nextMonth')} onClick={() => shift(1)} className="flex size-9 items-center justify-center rounded-full text-lg hover:bg-subtle">
            ›
          </button>
        </span>
      </div>
      <div className="grid grid-cols-7 text-center">
        {weekdays.map((w) => (
          <span key={w} className="eyebrow pb-1.5 text-[10px]">
            {w}
          </span>
        ))}
        {shown.map((d) => {
          const inMonth = d.slice(0, 7) === month;
          const selected = d === date;
          const inWeek = highlightWeek && startOfWeek(d) === selectedWeek;
          const weekday = (shown.indexOf(d) % 7) as number;
          return (
            <span key={d} className={`flex justify-center py-0.5 ${inWeek ? `bg-subtle ${weekday === 0 ? 'rounded-l-full' : ''} ${weekday === 6 ? 'rounded-r-full' : ''}` : ''}`}>
              <button
                type="button"
                onClick={() => onPick(d)}
                aria-current={selected ? 'date' : undefined}
                className={`flex size-9 items-center justify-center rounded-full text-sm tabular-nums transition-colors ${
                  selected && !highlightWeek
                    ? 'bg-ink font-semibold text-canvas'
                    : d === today
                      ? 'bg-accent font-semibold text-on-accent'
                      : inMonth
                        ? 'hover:bg-subtle'
                        : 'text-muted/50 hover:bg-subtle'
                }`}
              >
                {Number(d.slice(8))}
              </button>
            </span>
          );
        })}
      </div>
    </section>
  );
}

function Summary({ data, days, view, tz, showRevenue }: { data: CalendarData; days: string[]; view: 'dag' | 'week' | 'lijst'; tz: string; showRevenue: boolean }) {
  const t = useT();
  const list = data.appointments.filter((a) => days.includes(dateOf(a.startsAt, tz)) && a.status !== 'cancelled');
  const facts = [
    { label: t('cal.side.appointments'), value: String(list.length) },
    { label: t('cal.side.newClients'), value: String(list.filter((a) => a.newClient).length) },
    { label: t('cal.side.online'), value: String(list.filter((a) => a.source === 'online').length) },
    ...(showRevenue ? [{ label: t('cal.side.revenue'), value: formatEuro(list.filter((a) => a.status !== 'no_show').reduce((sum, a) => sum + a.priceCents, 0)).replace(/,00$/, '') }] : []),
  ];
  return (
    <section className={card}>
      <h2 className="eyebrow mb-3">{view === 'dag' ? t('cal.side.day') : t('cal.side.week')}</h2>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        {facts.map((f) => (
          <div key={f.label} className="flex flex-col">
            <dd className="display text-[30px] leading-none tabular-nums">{f.value}</dd>
            <dt className="mt-1 text-[13px] text-muted">{f.label}</dt>
          </div>
        ))}
      </dl>
    </section>
  );
}

function OpenGaps({ data, date, today, tz, now, staffIds, onSlot }: { data: CalendarData; date: string; today: string; tz: string; now: number; staffIds: string[]; onSlot: (staffId: string, date: string, minute: number) => void }) {
  const t = useT();
  const dayStart = Date.parse(instantAt(date, 0, tz));
  const dayEnd = Date.parse(instantAt(addDays(date, 1), 0, tz));
  const range = (r: { start: string; end: string }): Interval => ({ start: Math.max(dayStart, Date.parse(r.start)), end: Math.min(dayEnd, Date.parse(r.end)) });

  const people = useMemo(
    () =>
      data.staff
        .filter((s) => staffIds.includes(s.id))
        .map((s) => {
          const working = s.scheduled.map(range).filter((r) => r.end > r.start);
          const busy = [
            ...data.appointments.filter((a) => a.status !== 'cancelled').flatMap((a) => a.parts.filter((p) => p.staffMemberId === s.id).map(range)),
            ...data.blocks.filter((b) => b.staffMemberId === null || b.staffMemberId === s.id).map(range),
          ];
          const gaps = openGaps(working, busy, { minMinutes: 30, from: date === today ? now : undefined });
          const over = working.length > 0 && date === today && Math.max(...working.map((w) => w.end)) <= now;
          return { ...s, working, gaps, over };
        })
        .filter((s) => s.working.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, date, today, now, staffIds.join()],
  );
  if (people.length === 0) return null;

  const length = (ms: number) => {
    const total = Math.round(ms / 60_000);
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h === 0 ? t('cal.side.minutes', { m }) : m === 0 ? t('cal.side.hours', { h }) : t('cal.side.hoursMinutes', { h, m });
  };

  return (
    <section className={card}>
      <h2 className="eyebrow">{t('cal.side.free')}</h2>
      <p className="mb-3 mt-1 text-[13px] text-muted">{t('cal.side.freeHint')}</p>
      <ul className="flex flex-col gap-3">
        {people.map((s) => (
          <li key={s.id} className="flex flex-col gap-1">
            <span className="flex items-center justify-between gap-2 text-[14px] font-semibold">
              <span className="flex items-center gap-2">
                <span className="size-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                {s.displayName}
              </span>
              {s.gaps.length === 0 && <span className="tag bg-subtle text-muted">{s.over ? t('cal.side.dayOver') : t('cal.side.full')}</span>}
            </span>
            {s.gaps.slice(0, 4).map((g) => (
              <button
                key={g.start}
                type="button"
                onClick={() => onSlot(s.id, date, minuteOfDay(g.start, tz))}
                className="flex items-center justify-between rounded-[10px] px-2.5 py-1.5 text-left text-[14px] tabular-nums transition-colors hover:bg-[var(--gap-tint)]"
                style={{ ['--gap-tint' as string]: `color-mix(in srgb, ${s.color} 14%, var(--color-surface))` }}
              >
                <span>
                  {formatTime(g.start, tz)} – {formatTime(g.end, tz)}
                </span>
                <span className="text-[13px] text-muted">{length(g.end - g.start)}</span>
              </button>
            ))}
          </li>
        ))}
      </ul>
    </section>
  );
}
